import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import interiorCatalogApi from "../services/interiorCatalogApi";


function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function NumberInput({ label, value, onChange, placeholder }) {
  return (
    <Field label={label}>
      <input
        type="number"
        value={value ?? ""}
        placeholder={placeholder || ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : Number(e.target.value))
        }
        className="w-full px-3 py-2 border rounded text-sm"
      />
    </Field>
  );
}

export default function InteriorHardwareDefinitionSetupPage() {
  const { definitionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null);
  const [params, setParams] = useState({});
  const [itemSearch, setItemSearch] = useState("");
  const [itemResults, setItemResults] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [componentFrames, setComponentFrames] = useState({});
  const [componentMetadataDrafts, setComponentMetadataDrafts] = useState({});

  const [itemMountingFrame, setItemMountingFrame] = useState({
  origin: { x: 0, y: 0, z: 0 },
  offset: { x: 0, y: 0, z: 0 },
  defaultRotation: { x: 0, y: 0, z: 0 },
  leftRotation: { x: 0, y: 0, z: 0 },
  rightRotation: { x: 0, y: 0, z: 0 },
});

async function saveComponentMetadata(component) {
  const draft = componentMetadataDrafts[component.id];

  if (!draft) return;

  const res = await interiorCatalogApi.updateDefinitionComponentMetadata(
    definitionId,
    component.id,
    draft
  );

  applySetupResponse(res);
}

async function clearComponentLocalParameters(component) {
  const ok = window.confirm(
    `Clear local parameters for ${component.asset_role}?`
  );

  if (!ok) return;

  const res = await interiorCatalogApi.clearDefinitionComponentLocalParameters(
    definitionId,
    component.id
  );

  applySetupResponse(res);
}

  async function load() {
  try {
    setLoading(true);

    const res = await interiorCatalogApi.getDefinitionHardwareSetup(definitionId);
    applySetupResponse(res);
  } catch (err) {
    console.error("load hardware setup error:", err);
    alert(err?.response?.data?.message || err?.message || "Load failed.");
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    load();
  }, [definitionId]);

  useEffect(() => {
    const text = itemSearch.trim();

    if (!text) {
      setItemResults([]);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const res = await interiorCatalogApi.listFamilyItems({
          search: text,
          status: "active",
          limit: 50,
          offset: 0,
        });

        setItemResults(res?.items || []);
      } catch (err) {
        console.error("item search error:", err);
        setItemResults([]);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [itemSearch]);

  function patchParam(key, value) {
    setParams((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function patchComponentFrame(componentId, section, axis, value) {
  setComponentFrames((prev) => ({
    ...prev,
    [componentId]: {
      ...(prev[componentId] || {}),
      [section]: {
        ...((prev[componentId] || {})[section] || {}),
        [axis]: Number(value) || 0,
      },
    },
  }));
}

function patchItemMountingFrame(section, axis, value) {
  setItemMountingFrame((prev) => ({
    ...prev,
    [section]: {
      ...(prev[section] || {}),
      [axis]: Number(value) || 0,
    },
  }));
}

async function saveItemMountingFrame() {
  if (!setup?.linkedMounting?.id) {
    alert("Linked item has no item mounting.");
    return;
  }

  const res =
    await interiorCatalogApi.updateDefinitionLinkedItemMountingReferenceFrame(
      definitionId,
      itemMountingFrame
    );

  applySetupResponse(res);
}

async function saveComponentFrame(component) {
  const mountingId = component.asset_mounting?.id;

  if (!mountingId) {
    alert("This component has no asset mounting.");
    return;
  }

  const payload = componentFrames[component.id];

  const res =
    await interiorCatalogApi.updateDefinitionComponentAssetMountingReferenceFrame(
      definitionId,
      mountingId,
      payload
    );

  applySetupResponse(res);
}

function buildComponentFrames(components = []) {
  return Object.fromEntries(
    components.map((component) => {
      const frame = component.asset_mounting?.reference_frame_json || {};

      return [
        component.id,
        {
          offset: frame.offset || { x: 0, y: 0, z: 0 },
          defaultRotation: frame.defaultRotation || { x: 0, y: 0, z: 0 },
          leftRotation: frame.leftRotation || { x: 0, y: 0, z: 0 },
          rightRotation: frame.rightRotation || { x: 0, y: 180, z: 0 },
        },
      ];
    })
  );
}

function buildItemMountingFrame(linkedMounting) {
  const frame = linkedMounting?.reference_frame_json || {};

  return {
    origin: frame.origin || { x: 0, y: 0, z: 0 },
    offset: frame.offset || { x: 0, y: 0, z: 0 },
    defaultRotation: frame.defaultRotation || { x: 0, y: 0, z: 0 },
    leftRotation: frame.leftRotation || { x: 0, y: 0, z: 0 },
    rightRotation: frame.rightRotation || { x: 0, y: 180, z: 0 },
  };
}

function applySetupResponse(res) {
  setSetup(res);
  setParams({ ...(res?.definition?.default_parameters || {}) });
  setSelectedItemId(res?.definition?.catalog_item_id || "");
  setComponentFrames(buildComponentFrames(res?.components || []));
  setItemMountingFrame(buildItemMountingFrame(res?.linkedMounting));
  setComponentMetadataDrafts(
  Object.fromEntries(
    (res?.components || []).map((component) => [
      component.id,
      {
        position: component.metadata_json?.position || { x: 0, y: 0, z: 0 },
        rotation: component.metadata_json?.rotation || { x: 0, y: 0, z: 0 },
        parameters: component.metadata_json?.parameters || {},
      },
    ])
  )
);
}

  async function saveParameters() {
  const res = await interiorCatalogApi.updateDefinitionDefaultParameters(
    definitionId,
    params
  );

  applySetupResponse(res);
}

  async function loadFromLinkedItem() {
  const res = await interiorCatalogApi.loadDefinitionParametersFromLinkedItem(
    definitionId
  );

  applySetupResponse(res);
}

  async function linkItem() {
  const res = await interiorCatalogApi.linkDefinitionCatalogItem(
    definitionId,
    selectedItemId ? Number(selectedItemId) : null
  );

  applySetupResponse(res);
}

  async function applyRunnerFormulas() {
  const res = await interiorCatalogApi.applyRunnerFormulas(definitionId);
  applySetupResponse(res);
}

  const definition = setup?.definition;
  const linkedItem = setup?.linkedItem;
  const components = setup?.components || [];
  const validation = setup?.validation || [];

  if (loading) {
    return (
      <div className="p-6 text-slate-500">
        Loading hardware setup...
      </div>
    );
  }

  if (!definition) {
    return (
      <div className="p-6 text-red-600">
        Definition not found.
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] overflow-hidden flex flex-col bg-slate-100">
      <div className="h-14 border-b bg-white px-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase text-slate-500">
            Hardware Definition Setup
          </div>
          <div className="font-semibold text-sm">
            {definition.name || definition.code}
          </div>
          <div className="text-xs text-slate-500">
            definition #{definition.id} · {definition.code}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate("/interior-editor/catalog")}
            className="px-3 py-1.5 border rounded bg-white text-sm"
          >
            Catalog
          </button>

          {linkedItem?.id ? (
            <button
              type="button"
              onClick={() =>
                navigate(`/interior-editor/catalog/items/${linkedItem.id}/assembly`)
              }
              className="px-3 py-1.5 border rounded bg-white text-sm"
            >
              Open item assembly
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-1.5 border rounded bg-white text-sm"
          >
            Back
          </button>

          <button
  type="button"
  onClick={load}
  className="px-3 py-1.5 border rounded bg-white text-sm"
>
  Refresh
</button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 grid grid-cols-[360px_minmax(0,1fr)] gap-4">
        <aside className="space-y-4">
          <div className="border rounded bg-white p-3 space-y-2">
            <div className="font-semibold text-sm">Definition</div>
            <div className="text-xs text-slate-500">Code</div>
            <div className="font-mono text-xs">{definition.code}</div>
            <div className="text-xs text-slate-500">Category</div>
            <div className="font-mono text-xs">{definition.category || "—"}</div>
            <div className="text-xs text-slate-500">Node type</div>
            <div className="font-mono text-xs">{definition.node_type}</div>
          </div>

          <div className="border rounded bg-white p-3 space-y-3">
            <div>
              <div className="font-semibold text-sm">Linked item</div>
              <div className="text-xs text-slate-500">
                Item used as master source for parameters and assembly.
              </div>
            </div>

            {linkedItem ? (
              <div className="rounded border bg-slate-50 p-2 text-xs">
                <div className="font-semibold">{linkedItem.name}</div>
                <div>{linkedItem.code}</div>
                <div>SKU: {linkedItem.sku || "—"}</div>
                <div>Asset: {linkedItem.asset_id || "—"}</div>
              </div>
            ) : (
              <div className="text-sm text-amber-600">
                No linked item.
              </div>
            )}

            <Field label="Search item">
              <input
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder="Search item by code/name/SKU..."
                className="w-full px-3 py-2 border rounded text-sm"
              />
            </Field>

            <Field label="Select linked item">
              <select
                value={selectedItemId || ""}
                onChange={(e) => setSelectedItemId(e.target.value)}
                className="w-full px-3 py-2 border rounded bg-white text-sm"
              >
                <option value="">No linked item</option>

                {linkedItem ? (
                  <option value={linkedItem.id}>
                    Current: {linkedItem.code} · {linkedItem.name}
                  </option>
                ) : null}

                {itemResults.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.code} · {item.name}
                  </option>
                ))}
              </select>
            </Field>

            <button
              type="button"
              onClick={linkItem}
              className="w-full px-3 py-2 rounded bg-blue-600 text-white text-sm"
            >
              Link selected item
            </button>
          </div>

          <div className="border rounded bg-white p-3 space-y-2">
            <div className="font-semibold text-sm">Validation</div>

            {validation.map((check) => (
              <div
                key={check.key}
                className={`rounded border p-2 text-xs ${
                  check.ok
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                <div className="font-semibold">
                  {check.ok ? "✓" : "×"} {check.label}
                </div>
                <div>{check.message}</div>
              </div>
            ))}
          </div>
        </aside>

        <main className="space-y-4">
          <div className="border rounded bg-white p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">Runner parameters</div>
                <div className="text-xs text-slate-500">
                  Values used by drawer resolver and runner assembly.
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={loadFromLinkedItem}
                  className="px-3 py-1.5 border rounded bg-white text-sm"
                >
                  Load from linked item
                </button>

                <button
                  type="button"
                  onClick={saveParameters}
                  className="px-3 py-1.5 rounded bg-green-600 text-white text-sm"
                >
                  Save parameters
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <NumberInput
                label="Runner length"
                value={params.runnerLength}
                onChange={(v) => patchParam("runnerLength", v)}
                placeholder="550"
              />

              <NumberInput
                label="Drawer depth"
                value={params.drawerDepth}
                onChange={(v) => patchParam("drawerDepth", v)}
                placeholder="540"
              />

              <NumberInput
                label="Runner height"
                value={params.runnerHeight}
                onChange={(v) => patchParam("runnerHeight", v)}
                placeholder="42.8"
              />

              <NumberInput
                label="Side clearance"
                value={params.sideClearance}
                onChange={(v) => patchParam("sideClearance", v)}
                placeholder="7.5"
              />

              <NumberInput
                label="Minimum cabinet depth"
                value={params.minimumCabinetDepth}
                onChange={(v) => patchParam("minimumCabinetDepth", v)}
                placeholder="553"
              />

              <NumberInput
                label="Depth reduction"
                value={params.drawerDepthReduction}
                onChange={(v) => patchParam("drawerDepthReduction", v)}
                placeholder="10"
              />
            </div>
          </div>

          <div className="border rounded bg-white p-4 space-y-3">
  <div className="flex items-center justify-between">
    <div>
      <div className="font-semibold text-sm">
        Linked item mounting correction
      </div>
      <div className="text-xs text-slate-500">
        Transform applied to the whole linked item mounting before component formulas.
      </div>
    </div>

    <div className="border-t pt-3 mt-3">
  <div className="font-semibold text-xs text-slate-600 mb-2">
    Work plane
  </div>

  <div className="grid grid-cols-3 gap-3">
    <div>
      <label className="text-xs text-slate-500">Plane</label>
      <select
        value={setup?.linkedMounting?.placement_rules_json?.workPlane?.plane || "xy"}
        className="w-full px-2 py-1 border rounded text-sm bg-white"
        disabled
      >
        <option value="xy">XY</option>
        <option value="xz">XZ</option>
        <option value="yz">YZ</option>
      </select>
    </div>

    <div>
      <label className="text-xs text-slate-500">Plane offset</label>
      <input
        value={setup?.linkedMounting?.placement_rules_json?.workPlane?.offset ?? 0}
        readOnly
        className="w-full px-2 py-1 border rounded text-sm bg-slate-50"
      />
    </div>

    <div>
      <label className="text-xs text-slate-500">Lock drill axis</label>
      <input
        value={
          setup?.linkedMounting?.placement_rules_json?.workPlane?.lockDrillAxis !== false
            ? "yes"
            : "no"
        }
        readOnly
        className="w-full px-2 py-1 border rounded text-sm bg-slate-50"
      />
    </div>
  </div>
</div>

    {setup?.linkedMounting?.id ? (
      <button
        type="button"
        onClick={() =>
          window.open(
            `/interior-editor/catalog/items/${linkedItem.id}/mounting`,
            "_blank"
          )
        }
        className="px-3 py-1.5 border rounded bg-white text-xs text-blue-700"
      >
        Open item mounting
      </button>
    ) : null}
  </div>

  {!setup?.linkedMounting?.id ? (
    <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
      No item mounting exists for linked item.
    </div>
  ) : (
    <>
      <div className="grid grid-cols-3 gap-3">
        {["x", "y", "z"].map((axis) => (
          <div key={`item_offset_${axis}`}>
            <label className="text-xs text-slate-500">
              Offset {axis.toUpperCase()}
            </label>
            <input
              type="number"
              value={itemMountingFrame.offset?.[axis] ?? 0}
              onChange={(e) =>
                patchItemMountingFrame("offset", axis, e.target.value)
              }
              className="w-full px-2 py-1 border rounded text-sm"
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {["x", "y", "z"].map((axis) => (
          <div key={`item_rot_${axis}`}>
            <label className="text-xs text-slate-500">
              Default rotation {axis.toUpperCase()}
            </label>
            <input
              type="number"
              value={itemMountingFrame.defaultRotation?.[axis] ?? 0}
              onChange={(e) =>
                patchItemMountingFrame(
                  "defaultRotation",
                  axis,
                  e.target.value
                )
              }
              className="w-full px-2 py-1 border rounded text-sm"
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={saveItemMountingFrame}
        className="px-3 py-1.5 rounded bg-green-600 text-white text-sm"
      >
        Save item mounting correction
      </button>
    </>
  )}
</div>

          <div className="border rounded bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">Assembly formulas</div>
                <div className="text-xs text-slate-500">
                  Left/right component formulas for this linked item.
                </div>
              </div>

              <button
                type="button"
                onClick={applyRunnerFormulas}
                className="px-3 py-1.5 rounded bg-indigo-600 text-white text-sm"
              >
                Apply standard runner formulas
              </button>
            </div>

            <div className="border rounded bg-white p-4 space-y-3">
  <div>
    <div className="font-semibold text-sm">
      Component asset mounting correction
    </div>
    <div className="text-xs text-slate-500">
      Local GLB origin/offset correction for left/right component assets.
    </div>
  </div>

  <div className="space-y-3">
    {components.map((component) => {
      const frame =
        componentFrames[component.id] || {
          offset: { x: 0, y: 0, z: 0 },
          defaultRotation: { x: 0, y: 0, z: 0 },
        };

      const mounting = component.asset_mounting;

      return (
        <div
          key={component.id}
          className="border rounded p-3 bg-slate-50 space-y-3"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-sm">
                {component.asset_role}
              </div>
              <div className="text-xs text-slate-500">
                Asset: {component.asset?.code || component.asset_id || "—"}
              </div>
              <div className="text-xs text-slate-500">
                Mounting: {mounting?.id || "missing"}
              </div>
            </div>

            {component.asset_id ? (
              <button
                type="button"
                onClick={() =>
                  window.open(
                    `/interior-editor/assets/${component.asset_id}/mounting`,
                    "_blank"
                  )
                }
                className="px-3 py-1.5 border rounded bg-white text-xs text-blue-700"
              >
                Open asset mounting
              </button>
            ) : null}
          </div>

          <div className="border rounded bg-white p-3 space-y-2">
  <div className="font-semibold text-xs text-slate-600">
    Component local parameters
  </div>

  <pre className="text-xs bg-slate-100 p-2 rounded overflow-auto">
    {JSON.stringify(component.metadata_json?.parameters || {}, null, 2)}
  </pre>

  {Object.keys(component.metadata_json?.parameters || {}).length ? (
    <button
      type="button"
      onClick={() => clearComponentLocalParameters(component)}
      className="px-3 py-1.5 border rounded bg-white text-xs text-red-600"
    >
      Clear local parameters
    </button>
  ) : (
    <div className="text-xs text-slate-400">
      No local parameter overrides.
    </div>
  )}
</div>

<div className="border rounded bg-white p-3 text-xs">
  <div className="font-semibold mb-2">Transform stack</div>

  <div className="border rounded bg-white p-3 space-y-2">
  <div className="font-semibold text-xs text-slate-600">
    Edit component formula
  </div>

  <div className="grid grid-cols-3 gap-2">
    {["x", "y", "z"].map((axis) => (
      <div key={`pos_${component.id}_${axis}`}>
        <label className="text-xs text-slate-500">
          Position {axis.toUpperCase()}
        </label>
        <input
          value={
            componentMetadataDrafts[component.id]?.position?.[axis] ?? ""
          }
          onChange={(e) =>
            setComponentMetadataDrafts((prev) => ({
              ...prev,
              [component.id]: {
                ...(prev[component.id] || {}),
                position: {
                  ...((prev[component.id] || {}).position || {}),
                  [axis]: e.target.value,
                },
              },
            }))
          }
          className="w-full px-2 py-1 border rounded text-sm font-mono"
        />
      </div>
    ))}
  </div>

  <div className="grid grid-cols-3 gap-2">
    {["x", "y", "z"].map((axis) => (
      <div key={`rot_${component.id}_${axis}`}>
        <label className="text-xs text-slate-500">
          Rotation {axis.toUpperCase()}
        </label>
        <input
          type="number"
          value={
            componentMetadataDrafts[component.id]?.rotation?.[axis] ?? 0
          }
          onChange={(e) =>
            setComponentMetadataDrafts((prev) => ({
              ...prev,
              [component.id]: {
                ...(prev[component.id] || {}),
                rotation: {
                  ...((prev[component.id] || {}).rotation || {}),
                  [axis]: Number(e.target.value) || 0,
                },
              },
            }))
          }
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>
    ))}
  </div>

  <Field label="Local parameters JSON">
  <textarea
    value={JSON.stringify(
      componentMetadataDrafts[component.id]?.parameters || {},
      null,
      2
    )}
    onChange={(e) => {
      try {
        const parsed = JSON.parse(e.target.value || "{}");

        setComponentMetadataDrafts((prev) => ({
          ...prev,
          [component.id]: {
            ...(prev[component.id] || {}),
            parameters: parsed,
          },
        }));
      } catch {
        // Ne radimo ništa dok JSON nije validan
      }
    }}
    className="w-full min-h-[100px] px-2 py-1 border rounded text-xs font-mono"
  />
</Field>

<button
  type="button"
  onClick={() =>
    setComponentMetadataDrafts((prev) => ({
      ...prev,
      [component.id]: {
        position: component.metadata_json?.position || { x: 0, y: 0, z: 0 },
        rotation: component.metadata_json?.rotation || { x: 0, y: 0, z: 0 },
        parameters: component.metadata_json?.parameters || {},
      },
    }))
  }
  className="px-3 py-1.5 border rounded bg-white text-xs"
>
  Reset formula draft
</button>

  <button
    type="button"
    onClick={() => saveComponentMetadata(component)}
    className="px-3 py-1.5 rounded bg-green-600 text-white text-sm"
  >
    Save component formula
  </button>
</div>

  <div className="grid grid-cols-[180px_minmax(0,1fr)] gap-y-1">
    <div className="text-slate-500">Definition params</div>
    <div className="font-mono">
      runnerLength={params.runnerLength ?? "—"} · drawerDepth=
      {params.drawerDepth ?? "—"} · sideClearance=
      {params.sideClearance ?? "—"}
    </div>

    <div className="text-slate-500">Item mounting offset</div>
    <div className="font-mono">
      {JSON.stringify(itemMountingFrame.offset || {})}
    </div>

    <div className="text-slate-500">Component formula</div>
    <div className="font-mono">
      X: {component.metadata_json?.position?.x ?? "—"} · Y:{" "}
      {component.metadata_json?.position?.y ?? "—"} · Z:{" "}
      {component.metadata_json?.position?.z ?? "—"}
    </div>

    <div className="text-slate-500">Component rotation</div>
    <div className="font-mono">
      {JSON.stringify(component.metadata_json?.rotation || {})}
    </div>

    <div className="text-slate-500">Local parameters</div>
    <div className="font-mono">
      {JSON.stringify(component.metadata_json?.parameters || {})}
    </div>

    <div className="text-slate-500">Asset mounting offset</div>
    <div className="font-mono">
      {JSON.stringify(
        component.asset_mounting?.reference_frame_json?.offset || {}
      )}
    </div>

    <div className="text-slate-500">Asset mounting rotation</div>
    <div className="font-mono">
      {JSON.stringify(
        component.asset_mounting?.reference_frame_json?.defaultRotation || {}
      )}
    </div>
  </div>
</div>

          {!mounting ? (
            <div className="text-xs text-red-600">
              No asset mounting exists for this component asset.
            </div>
          ) : (
            <>
              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1">
                  Offset
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {["x", "y", "z"].map((axis) => (
                    <div key={axis}>
                      <label className="text-xs text-slate-500">
                        {axis.toUpperCase()}
                      </label>
                      <input
                        type="number"
                        value={frame.offset?.[axis] ?? 0}
                        onChange={(e) =>
                          patchComponentFrame(
                            component.id,
                            "offset",
                            axis,
                            e.target.value
                          )
                        }
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1">
                  Default rotation
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {["x", "y", "z"].map((axis) => (
                    <div key={axis}>
                      <label className="text-xs text-slate-500">
                        {axis.toUpperCase()}
                      </label>
                      <input
                        type="number"
                        value={frame.defaultRotation?.[axis] ?? 0}
                        onChange={(e) =>
                          patchComponentFrame(
                            component.id,
                            "defaultRotation",
                            axis,
                            e.target.value
                          )
                        }
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => saveComponentFrame(component)}
                className="px-3 py-1.5 rounded bg-green-600 text-white text-sm"
              >
                Save correction
              </button>
            </>
          )}
        </div>
        
      );
    })}
  </div>
</div>

            <div className="border rounded overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 border-b">Role</th>
                    <th className="text-left p-2 border-b">Asset</th>
                    <th className="text-left p-2 border-b">Position X</th>
                    <th className="text-left p-2 border-b">Position Y</th>
                    <th className="text-left p-2 border-b">Position Z</th>
                  </tr>
                </thead>

                <tbody>
                  {components.map((component) => (
                    <tr key={component.id} className="border-b">
                      <td className="p-2">{component.asset_role}</td>
                      <td className="p-2">
                        {component.asset?.code || component.asset_id || "—"}
                      </td>
                      <td className="p-2 font-mono text-xs">
                        {component.metadata_json?.position?.x ?? "—"}
                      </td>
                      <td className="p-2 font-mono text-xs">
                        {component.metadata_json?.position?.y ?? "—"}
                      </td>
                      <td className="p-2 font-mono text-xs">
                        {component.metadata_json?.position?.z ?? "—"}
                      </td>
                    </tr>
                  ))}

                  {!components.length ? (
                    <tr>
                      <td className="p-3 text-slate-400" colSpan={5}>
                        No assembly components for linked item.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border rounded bg-slate-900 p-4">
            <div className="font-semibold text-sm text-white mb-2">
              Raw setup JSON
            </div>
            <pre className="text-xs text-slate-200 overflow-auto max-h-[360px]">
              {JSON.stringify(setup, null, 2)}
            </pre>
          </div>
        </main>
      </div>
    </div>
  );
}